import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  ChevronRight,
  CreditCard,
  ShieldCheck,
  Wallet,
  Receipt,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Building2,
  Home,
} from "lucide-react";
import { useAuth0 } from "@auth0/auth0-react";
import { getPagosSocios, getDeudaSocio, getSocioPagosConSos } from "../services/cuotasService.tsx";
import { obtenerCajasDeSocio } from "../services/cajaService.tsx";
import { obtenerServiciosCatalogo } from "../services/serviciosAsociacionService.ts";

type PagoItem = {
  id: string | number;
  tipo: "cuota" | "servicio" | "caja";
  titulo: string;
  detalle?: string;
  importe: number;
  estado: "Pendiente" | "Pagada" | "Por cobrar" | string;
  vencimiento?: string;
};

type MovimientoSosItem = {
  id?: string | number;
  idcomprobante?: string | number;
  fecha?: string;
  clipro?: string;
  memo?: string;
  fcncnd?: string;
  letra?: string;
  sucursal?: number;
  numero?: number;
  montodebe?: number;
  montohaber?: number;
  montosaldo?: number;
  idtipo_operacion?: number;
  tipo_movimiento?: "FACTURA" | "RECIBO" | "OTRO" | string;
  comprobante_numero?: string;
  factura_referencia?: string;
  referencia?: string;
  factura?: string;
};

const tipoColor: Record<PagoItem["tipo"], string> = {
  cuota: "bg-indigo-50 text-indigo-700 border-indigo-200",
  servicio: "bg-emerald-50 text-emerald-700 border-emerald-200",
  caja: "bg-amber-50 text-amber-700 border-amber-200",
};

const InfoBadge = ({ label, value }: { label: string; value: string | number }) => (
  <div className="rounded-lg bg-white/15 px-3 py-2 text-xs font-semibold text-white shadow-sm backdrop-blur">
    <p className="uppercase tracking-wide text-white/80">{label}</p>
    <p className="text-sm">{value}</p>
  </div>
);

const estadoBadge = (estado: string) => {
  const est = estado.toLowerCase();
  if (est.includes("pag")) return "bg-emerald-100 text-emerald-700 border border-emerald-200";
  if (est.includes("pend") || est.includes("por cobrar"))
    return "bg-amber-100 text-amber-700 border border-amber-200";
  return "bg-rose-100 text-rose-700 border border-rose-200";
};

export default function PaymentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessTokenSilently } = useAuth0();
  const mpBackendBase =
    process.env.REACT_APP_MP_BACKEND_URL ||
    process.env.REACT_APP_BACK_URL ||
    `${window.location.protocol}//${window.location.hostname}:5000`;
  const [items, setItems] = useState<PagoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<"todos" | PagoItem["tipo"]>("todos");
  const [selectedDocType, setSelectedDocType] = useState<"todos" | "FACTURA" | "RECIBO">("todos");
  const [deudaTotal, setDeudaTotal] = useState<number | null>(null);
  const [movimientosSos, setMovimientosSos] = useState<MovimientoSosItem[]>([]);
  const [movimientosLoading, setMovimientosLoading] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [payingId, setPayingId] = useState<string | number | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);
  const [sortFecha, setSortFecha] = useState<"desc" | "asc">("desc");
  const [expandedFacturas, setExpandedFacturas] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tipo = params.get("tipo");
    if (tipo === "servicio" || tipo === "cuota" || tipo === "caja" || tipo === "todos") {
      setSelectedType(tipo as any);
      setPage(1);
    }
  }, [location.search]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setMovimientosLoading(true);
      setError(null);
      try {
        const stored = localStorage.getItem("socioSeleccionado");
        if (!stored) throw new Error("No hay socio seleccionado");
        const socio = JSON.parse(stored);
        const socioId = socio?.socio_id;
        if (!socioId) throw new Error("No se encontró el ID del socio");
        const token = await getAccessTokenSilently();

        // Fase 1: cargar rápido lo principal
        const [cuotasRes, deudaRes] = await Promise.allSettled([
          getPagosSocios(socioId, getAccessTokenSilently),
          getDeudaSocio(socioId, getAccessTokenSilently),
        ]);

        const cuotas = cuotasRes.status === 'fulfilled' ? cuotasRes.value : [];
        const deuda = deudaRes.status === 'fulfilled' ? deudaRes.value : null;

        // Fase 2: cargar secundarios sin bloquear render principal
        const secondaryPromise = Promise.allSettled([
          obtenerCajasDeSocio(socioId, token),
          obtenerServiciosCatalogo(token),
          getSocioPagosConSos(socioId, getAccessTokenSilently, { includeSosMovimientos: true }),
        ]);

        const totalDeuda =
          typeof deuda?.deuda_total === "number"
            ? deuda.deuda_total
            : typeof deuda?.total === "number"
            ? deuda.total
            : null;
        setDeudaTotal(totalDeuda);

        const mapCuotas: PagoItem[] = (cuotas || []).map((c: any) => ({
          id: c.id ?? `${c.mes}-${c.año}`,
          tipo: "cuota",
          titulo: `${c.mes} ${c.año}`,
          detalle: `Vence: ${c.fechaVencimiento}`,
          importe: Number(c.importe ?? 0),
          estado: c.estado || "Pendiente",
          vencimiento: c.fechaVencimiento,
        }));

        // render inicial rápido con cuotas
        setItems(mapCuotas);
        setLoading(false);

        const [cajasRes, serviciosRes, socioDetalleRes] = await secondaryPromise;
        const cajas = cajasRes.status === 'fulfilled' ? cajasRes.value : [];
        const servicios = serviciosRes.status === 'fulfilled' ? serviciosRes.value : [];
        const socioPagosDetalle = socioDetalleRes.status === 'fulfilled' ? socioDetalleRes.value : null;
        const rawMovimientosSos = Array.isArray(socioPagosDetalle?.movimientos_sos)
          ? socioPagosDetalle.movimientos_sos
          : [];
        setLastSyncAt(socioPagosDetalle?.movimientos_sos_info?.last_sync_at || null);

        const mapCajas: PagoItem[] = (cajas || []).map((c: any) => ({
          id: c.id ?? c.caja_id ?? c.numero,
          tipo: "caja",
          titulo: `Caja ${c.numero || c.id || ""}`,
          detalle: c.estado || "Caja de seguridad",
          importe: Number(c.caja.tamano.precio_mensual),
          estado: c.estado || "Pendiente",
        }));

        const mapServicios: PagoItem[] = (servicios || []).map((s: any) => ({
          id: s.id,
          tipo: "servicio",
          titulo: s.nombre,
          detalle: s.categoria || s.descripcion,
          importe: Number(s.importe ?? 0),
          estado: s.activo === false ? "Pendiente" : "Por cobrar",
        }));

        const allItems = [...mapCuotas, ...mapCajas, ...mapServicios];
        setItems(allItems);

        if (rawMovimientosSos.length > 0) {
          setMovimientosSos(rawMovimientosSos);
        } else {
          // Sin datos contables de SOS: evitamos inventar deuda con fuentes locales.
          setMovimientosSos([]);
        }
      } catch (err: any) {
        setError(err?.message || "No se pudieron cargar los datos de pagos.");
      } finally {
        setLoading(false);
        setMovimientosLoading(false);
      }
    };
    load();
  }, [getAccessTokenSilently]);

  const inferTipoMovimiento = (m: MovimientoSosItem): PagoItem["tipo"] => {
    const text = `${m.memo || ""} ${m.fcncnd || ""} ${m.letra || ""}`.toLowerCase();

    // Regla estricta para evitar falsos positivos de "caja":
    // 1) cuota por id de operación (fuente más confiable)
    // 2) caja solo por id de operación explícito
    // 3) fallback textual mínimo solo para cuota
    const op = Number(m.idtipo_operacion);
    if ([12, 14].includes(op)) return "cuota";
    if ([4].includes(op)) return "caja";

    if (text.includes("cuota") || text.includes("social")) return "cuota";

    return "servicio";
  };

  const getTipoOperacionLabel = (m: MovimientoSosItem): string | null => {
    const op = Number(m.idtipo_operacion);
    if (!Number.isFinite(op) || op <= 0) return null;

    const map: Record<number, string> = {
      2: "Factura",
      4: "Caja",
      12: "Cuota",
      14: "Cuota",
    };

    return map[op] || null;
  };

  const extractCae = (m: MovimientoSosItem): string | null => {
    const text = `${m.referencia || ""} ${m.memo || ""}`;
    const match = text.match(/CAE[:\s]*([0-9]{8,20})/i);
    return match?.[1] || null;
  };

  const getDetalleUnificado = (m: MovimientoSosItem): string => {
    const tipoOp = getTipoOperacionLabel(m);
    const cae = extractCae(m);

    const partes = [
      m.referencia ? `Ref: ${m.referencia}` : null,
      tipoOp ? `Operación: ${tipoOp}` : null,
      cae ? `CAE: ${cae}` : null,
    ].filter(Boolean);

    return partes.length ? partes.join(" · ") : "Sin detalle";
  };

  const getComprobante = (m: MovimientoSosItem) => {
    if (m.comprobante_numero) return m.comprobante_numero;
    const comp = [m.fcncnd, m.letra, m.sucursal, m.numero]
      .filter((x) => x !== null && x !== undefined && String(x) !== "")
      .join("-");
    return comp || m.idcomprobante || m.id || "—";
  };

  const getFacturaKey = (m: MovimientoSosItem): string => {
    const tipoSos = String(m.tipo_movimiento || "").toUpperCase();
    if (tipoSos === "RECIBO") {
      return String(m.factura_referencia || m.factura || "SIN_FACTURA");
    }
    return String(m.factura || getComprobante(m));
  };

  const filtrados = useMemo(() => {
    const filtered = movimientosSos.filter((m) => {
      const tipo = inferTipoMovimiento(m);
      const tipoSos = String(m.tipo_movimiento || "").toUpperCase();
      const passTipo = selectedType === "todos" ? true : tipo === selectedType;
      const passDoc = selectedDocType === "todos" ? true : tipoSos === selectedDocType;
      return passTipo && passDoc;
    });

    return filtered.sort((a, b) => {
      const aTime = new Date(a.fecha || 0).getTime();
      const bTime = new Date(b.fecha || 0).getTime();
      return sortFecha === "desc" ? bTime - aTime : aTime - bTime;
    });
  }, [movimientosSos, selectedType, selectedDocType, sortFecha]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filtrados.length / pageSize));
  }, [filtrados.length, pageSize]);

  const paginados = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtrados.slice(start, start + pageSize);
  }, [filtrados, page, pageSize]);

  const rangoInicio = filtrados.length ? (page - 1) * pageSize + 1 : 0;
  const rangoFin = filtrados.length ? Math.min(page * pageSize, filtrados.length) : 0;

  const resumenPorFactura = useMemo(() => {
    const map: Record<string, { recibos: number; cobrado: number; facturado: number }> = {};

    paginados.forEach((m) => {
      const key = getFacturaKey(m);
      if (!map[key]) map[key] = { recibos: 0, cobrado: 0, facturado: 0 };

      const tipoSos = String(m.tipo_movimiento || "").toUpperCase();
      const haber = Number(m.montohaber || 0);
      const debe = Number(m.montodebe || 0);

      if (tipoSos === "RECIBO") {
        map[key].recibos += 1;
        map[key].cobrado += haber > 0 ? haber : 0;
      }

      if (tipoSos === "FACTURA") {
        map[key].facturado += debe > 0 ? debe : 0;
      }
    });

    return map;
  }, [paginados]);

  const totales = useMemo(() => {
    const base = { cuota: 0, servicio: 0, caja: 0 };

    movimientosSos.forEach((m) => {
      const tipo = inferTipoMovimiento(m);
      const saldo = Number(m.montosaldo || 0);
      const debe = Number(m.montodebe || 0);
      const haber = Number(m.montohaber || 0);
      const pendiente = saldo > 0 ? saldo : Math.max(0, debe - haber);
      if (pendiente > 0) base[tipo] += pendiente;
    });

    return {
      cuota: Number(base.cuota.toFixed(2)),
      servicio: Number(base.servicio.toFixed(2)),
      caja: Number(base.caja.toFixed(2)),
    };
  }, [movimientosSos]);

  const deudaTotalUi = useMemo(() => {
    const total = Number(totales.cuota || 0) + Number(totales.servicio || 0) + Number(totales.caja || 0);
    return Number(total.toFixed(2));
  }, [totales]);

  const syncLabel = useMemo(() => {
    if (!lastSyncAt) return "Sin sincronización reciente";
    const last = new Date(lastSyncAt).getTime();
    if (Number.isNaN(last)) return "Sincronización reciente";
    const diffMin = Math.max(0, Math.floor((Date.now() - last) / 60000));
    if (diffMin < 1) return "Actualizado hace instantes";
    if (diffMin < 60) return `Actualizado hace ${diffMin} min`;
    const hours = Math.floor(diffMin / 60);
    return `Actualizado hace ${hours} h`;
  }, [lastSyncAt]);

  useEffect(() => {
    setPage(1);
  }, [selectedType, selectedDocType, items.length]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filtrados.length / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [filtrados.length, page, pageSize]);

  const pagarItem = async (item: PagoItem) => {
    try {
      console.log("Iniciando pago para item:", item);
      setPayingId(item.id);
      setPayError(null);
      const token = await getAccessTokenSilently().catch(() => null);
      const baseUrl = window.location.origin;
      const apiBase = mpBackendBase;
      const resp = await fetch(`${apiBase}/mercadopago/preferencias`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          pagoId: item.id,
          tipo: item.tipo,
          auto_return: "approved",
          back_urls: {
            success: `${baseUrl}/mercadopago/success`,
            failure: `${baseUrl}/mercadopago/failure`,
            pending: `${baseUrl}/mercadopago/failure`,
          },
          notification_url: `${apiBase}/mercadopago/webhook`,
        }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || "No se pudo generar la preferencia de pago");
      }
      const pref = await resp.json();
      const redirect = pref.init_point || pref.sandbox_init_point;
      if (!redirect) throw new Error("No se recibió init_point");
      window.location.href = redirect;
    } catch (err: any) {
      setPayError(err?.message || "No se pudo iniciar el pago.");
    } finally {
      setPayingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {payingId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-2xl">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-600">
              <Loader2 className="h-7 w-7 animate-spin" />
            </div>
            <h3 className="mt-4 text-xl font-semibold text-slate-900">Preparando el pago</h3>
            <p className="mt-2 text-sm text-slate-600">
              Estamos generando la preferencia y redirigiendo a Mercado Pago. No cierres esta ventana.
            </p>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-10 space-y-6">
        <nav className="flex mb-2" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/inicio" className="inline-flex text-sm font-medium text-blue-600 hover:text-blue-800">
                <Home className="w-4 h-4 mr-2" />
                Inicio
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <span className="ml-1 text-sm font-medium text-gray-600 md:ml-2">Pagos</span>
              </div>
            </li>
          </ol>
        </nav>

        <div className={`overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 text-white shadow-2xl ${deudaTotalUi > 0 ? 'ring-2 ring-amber-300/80' : ''}`}>
          <div className="flex flex-col gap-4 px-6 py-5 md:px-10 lg:px-12">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="space-y-1">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-indigo-100">
                  <CheckCircle2 className="h-4 w-4" />
                  Centro de pagos
                </p>
                <h1 className="text-2xl font-bold leading-tight sm:text-3xl">
                  Resumen de deuda
                </h1>
                <p className="text-xs text-indigo-100/90">{syncLabel}</p>
              </div>
              <p className="text-xs font-semibold text-indigo-100/95">
                {deudaTotalUi > 0
                  ? `Estado general: Tenés deuda: $${deudaTotalUi.toLocaleString("es-AR")}`
                  : 'Estado general: Sin deudas'}
              </p>
            </div>

            <div className="grid gap-2 sm:grid-cols-3">
              {loading
                ? Array.from({ length: 3 }).map((_, idx) => (
                    <div
                      key={`sk-${idx}`}
                      className="rounded-xl border border-white/20 bg-white/10 px-3 py-2"
                    >
                      <div className="h-3 w-24 rounded bg-white/30 animate-pulse" />
                      <div className="mt-2 h-6 w-20 rounded bg-white/40 animate-pulse" />
                    </div>
                  ))
                : [
                    { label: "Cuotas", value: totales.cuota },
                    { label: "Servicios", value: totales.servicio },
                    { label: "Cajas", value: totales.caja },
                  ].map((item) => {
                    const debtValue = Number(item.value || 0);
                    const hasDebt = debtValue > 0;
                    const isHighDebt = debtValue >= 50000;
                    const toneClass = !hasDebt
                      ? "border-emerald-200/60 bg-emerald-400/10"
                      : isHighDebt
                      ? "border-rose-300/70 bg-rose-400/10"
                      : "border-amber-300/70 bg-amber-300/10";

                    return (
                      <div
                        key={item.label}
                        className={`rounded-xl border px-3 py-2 shadow-sm ${toneClass}`}
                      >
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-100">
                          {item.label}
                        </p>
                        <p className="text-lg font-bold text-white">
                          {!hasDebt
                            ? "Sin deudas"
                            : `$${debtValue.toLocaleString("es-AR")}`}
                        </p>
                      </div>
                    );
                  })}
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-slate-600">
            <Loader2 className="h-5 w-5 animate-spin" />
            Cargando pagos...
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">Movimientos de cuenta</p>
              <p className="text-xs text-slate-600">
                Guía rápida: <strong>Factura</strong> = cargo (te suma deuda), <strong>Recibo</strong> = pago (te baja deuda). Estado <strong>Pendiente</strong> = falta pagar, <strong>Compensado</strong> = pago aplicado.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2 rounded-full bg-slate-50 px-2 py-1">
                {[
                  { key: "todos", label: "Todos" },
                  { key: "cuota", label: "Cuotas" },
                  { key: "servicio", label: "Servicios" },
                  { key: "caja", label: "Cajas" },
                ].map((f) => (
                  <button
                    key={f.key}
                    onClick={() => {
                      setSelectedType(f.key as any);
                      setPage(1);
                    }}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      selectedType === f.key
                        ? "bg-blue-600 text-white shadow-sm"
                        : "bg-white text-slate-700 border border-slate-200"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 rounded-full bg-slate-50 px-2 py-1">
                {[
                  { key: "todos", label: "Todos los documentos" },
                  { key: "FACTURA", label: "Solo facturas" },
                  { key: "RECIBO", label: "Solo recibos" },
                ].map((f) => (
                  <button
                    key={f.key}
                    onClick={() => {
                      setSelectedDocType(f.key as any);
                      setPage(1);
                    }}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      selectedDocType === f.key
                        ? "bg-indigo-600 text-white shadow-sm"
                        : "bg-white text-slate-700 border border-slate-200"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-3 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-600">
            <p>
              Mostrando {filtrados.length ? `${rangoInicio}-${rangoFin}` : 0} de {filtrados.length} movimientos
            </p>
            <div className="flex items-center gap-2">
              <label className="font-semibold" htmlFor="sort-fecha">Orden fecha</label>
              <select
                id="sort-fecha"
                value={sortFecha}
                onChange={(e) => {
                  setSortFecha(e.target.value as "desc" | "asc");
                  setPage(1);
                }}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 shadow-sm"
              >
                <option value="desc">Más recientes</option>
                <option value="asc">Más antiguos</option>
              </select>

              <label className="font-semibold" htmlFor="page-size">Por página</label>
              <select
                id="page-size"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 shadow-sm"
              >
                {[5, 10, 15, 20].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <button
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page === 1}
                className="rounded-lg border border-slate-200 px-2 py-1 font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
              >
                Anterior
              </button>
              <span className="px-1 font-semibold text-slate-700">{page}/{totalPages}</span>
              <button
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages}
                className="rounded-lg border border-slate-200 px-2 py-1 font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
              >
                Siguiente
              </button>
            </div>
          </div>

          {(loading || movimientosLoading) && (
            <div className="mb-3 flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700">
              <Loader2 className="h-4 w-4 animate-spin" />
              Obteniendo movimientos y cuotas... un segundito.
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Fecha</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Documento</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Comprobante</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Tipo</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Detalle</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Movimiento</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-600">Importe</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {(loading || movimientosLoading)
                  ? Array.from({ length: 8 }).map((_, idx) => (
                      <tr key={`skeleton-${idx}`} className="animate-pulse">
                        <td className="px-3 py-3"><div className="h-3 w-20 rounded bg-slate-200" /></td>
                        <td className="px-3 py-3"><div className="h-5 w-20 rounded-full bg-slate-200" /></td>
                        <td className="px-3 py-3"><div className="h-3 w-24 rounded bg-slate-200" /></td>
                        <td className="px-3 py-3"><div className="h-5 w-16 rounded-full bg-slate-200" /></td>
                        <td className="px-3 py-3"><div className="h-3 w-40 rounded bg-slate-200" /></td>
                        <td className="px-3 py-3"><div className="h-3 w-24 rounded bg-slate-200" /></td>
                        <td className="px-3 py-3"><div className="ml-auto h-3 w-16 rounded bg-slate-200" /></td>
                        <td className="px-3 py-3"><div className="h-5 w-20 rounded-full bg-slate-200" /></td>
                      </tr>
                    ))
                  : paginados.map((m, idx) => {
                      const tipo = inferTipoMovimiento(m);
                      const tipoSos = String(m.tipo_movimiento || '').toUpperCase();
                      const detalleUnificado = getDetalleUnificado(m);
                      const facturaKey = getFacturaKey(m);
                      const isRecibo = tipoSos === 'RECIBO';
                      const isFactura = tipoSos === 'FACTURA';
                      const resumenFactura = resumenPorFactura[facturaKey] || { recibos: 0, cobrado: 0, facturado: 0 };
                      const recibosCount = resumenFactura.recibos || 0;
                      const showRecibos = !!expandedFacturas[facturaKey];

                      if (isRecibo && facturaKey !== 'SIN_FACTURA' && !showRecibos) return null;

                      return (
                        <tr key={`${m.idcomprobante || m.id || "mov"}-${idx}`} className={isRecibo ? 'bg-slate-50/70' : ''}>
                          <td className="px-3 py-2 text-slate-700">
                            {m.fecha ? new Date(m.fecha).toLocaleDateString("es-AR") : "—"}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                              tipoSos === 'RECIBO'
                                ? 'bg-emerald-100 text-emerald-700'
                                : tipoSos === 'FACTURA'
                                ? 'bg-sky-100 text-sky-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {tipoSos === 'RECIBO' ? 'Recibo' : tipoSos === 'FACTURA' ? 'Factura' : 'Movimiento'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            <div className="flex items-center gap-2">
                              {isRecibo && <span className="text-slate-400">↳</span>}
                              <span>{getComprobante(m)}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            <div className="flex items-center gap-1 whitespace-nowrap">
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${tipoColor[tipo]}`}>
                                {tipo === "cuota" ? "Cuota" : tipo === "caja" ? "Caja" : "Servicio"}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            <div className="flex flex-wrap items-center gap-2">
                              <span>{detalleUnificado}</span>
                              {isFactura && recibosCount > 0 && (
                                <>
                                  <button
                                    onClick={() =>
                                      setExpandedFacturas((prev) => ({
                                        ...prev,
                                        [facturaKey]: !prev[facturaKey],
                                      }))
                                    }
                                    className="rounded border border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-100"
                                  >
                                    {showRecibos ? `Ocultar recibos (${recibosCount})` : `Ver recibos (${recibosCount})`}
                                  </button>
                                  <span className="rounded bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-700">
                                    Facturado: ${resumenFactura.facturado.toLocaleString("es-AR")}
                                  </span>
                                  <span className="rounded bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                    Cobrado: ${resumenFactura.cobrado.toLocaleString("es-AR")}
                                  </span>
                                  <span className="rounded bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                                    Saldo: ${Math.max(0, resumenFactura.facturado - resumenFactura.cobrado).toLocaleString("es-AR")}
                                  </span>
                                </>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {Number(m.montohaber || 0) > 0 ? "Pago recibido" : "Cargo generado"}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-900">
                            ${Number((m.montohaber || 0) > 0 ? m.montohaber : m.montodebe || 0).toLocaleString("es-AR")}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${(Number(m.montosaldo || 0) > 0) ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
                              {Number(m.montosaldo || 0) > 0 ? "Pendiente" : "Compensado"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
          </div>

          {!filtrados.length && !loading && !movimientosLoading && (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              No hay movimientos contables de SOS para mostrar. Ejecutá sincronización para ver deuda real.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
